"use client";

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { io, type Socket } from "socket.io-client";
import { useTranslation } from "react-i18next";
import {
  api,
  type MultiOutcomeHistorySeries,
  type PriceHistoryInterval,
  type PriceHistoryPoint,
} from "@/lib/api";
import { pickLocale } from "@/lib/format";
import { Skeleton } from "@/components/Skeleton";

interface Props {
  slug: string;
  viewerLocale: string;
}

interface IncomingTradeEvent {
  marketSlug: string;
}

const INTERVALS: { id: PriceHistoryInterval; label: string }[] = [
  { id: "1h", label: "1H" },
  { id: "6h", label: "6H" },
  { id: "1d", label: "1D" },
  { id: "1w", label: "1W" },
  { id: "1m", label: "1M" },
  { id: "all", label: "ALL" },
];

// Minimum total data points across all outcomes for the selected
// interval to be considered "renderable." Below this we walk one
// step wider in INTERVALS until we find a range with enough trades
// or run out of options. Stops the chart from rendering as an empty
// canvas on low-liquidity markets / fresh markets / quiet windows.
const MIN_VISIBLE_POINTS = 2;

function widerInterval(
  current: PriceHistoryInterval,
): PriceHistoryInterval | null {
  const order: PriceHistoryInterval[] = ["1h", "6h", "1d", "1w", "1m", "all"];
  const idx = order.indexOf(current);
  if (idx < 0 || idx === order.length - 1) return null;
  return order[idx + 1];
}

// Polling cadence per interval. Mirrors `LivePriceChart`'s shape so
// the two charts share the same liveness posture — sub-second WS
// updates when the pipe is healthy, polling fallback for the
// cloudflared tunnel windows that drop WS (#1607).
const POLL_INTERVAL_MS: Record<PriceHistoryInterval, number> = {
  "1h": 5_000,
  "6h": 10_000,
  "1d": 15_000,
  "1w": 30_000,
  "1m": 60_000,
  all: 60_000,
};

/**
 * STORY-C — Polymarket-parity multi-line price chart for multi-outcome
 * markets (Eurovision winner, strike-ladder, election-night clusters).
 *
 * One line per outcome — top 5 by volume + a synthetic "Other" rollup
 * for everything else. X-axis = time, Y-axis = 0-100% probability so
 * the lines sum-to-one (the user can eyeball the constraint and the
 * NO-side reads as the empty space above the headline outcomes).
 *
 * Mirrors `LivePriceChart`'s shape: time-range tabs at the bottom,
 * native SVG (no chart library), ResizeObserver-driven pixel sizing,
 * WS-driven live refetch + polling fallback. The two charts look
 * like siblings of the same chart family.
 *
 * # Color palette
 *
 * Eight distinct line colors, ordered for stable assignment so a
 * given outcome keeps the same color when the user changes the time
 * range. The first three are Granbay tokens; the remaining five are
 * Tailwind 500-scale stops picked for AAA contrast in both light and
 * dark themes:
 *
 *   1. `var(--color-yes)`     #2d8a5d  green
 *   2. `var(--color-no)`      #b34a4a  red
 *   3. `var(--color-brand)`   #0070f3  blue
 *   4.                        #a855f7  purple (Tailwind violet-500)
 *   5.                        #f59e0b  amber  (Tailwind amber-500)
 *   6.                        #06b6d4  cyan   (Tailwind cyan-500)
 *   7.                        #ec4899  pink   (Tailwind pink-500)
 *   8.                        #14b8a6  teal   (Tailwind teal-500)
 *
 * The "Other" rollup uses a neutral gray (`#6b7280`) with a dotted
 * stroke so it visually recedes from the headline outcomes.
 *
 * # Sparse-data handling
 *
 * If an outcome has fewer than 2 data points in the time range, its
 * line is rendered dotted and a "not enough history" tag appears on
 * its legend row. Outcomes with zero points are still listed in the
 * legend so the user knows what's missing — the legend never lies
 * about which outcomes exist on the market. NEVER fake data.
 */
export function MultiOutcomeLivePriceChart({ slug, viewerLocale }: Props) {
  const { t } = useTranslation();
  const [interval, setInterval] = useState<PriceHistoryInterval>("1d");
  // Actual interval the rendered chart is sourced from. May be wider
  // than the user's selection when the smart-fallback walks up the
  // INTERVALS ladder looking for data.
  const [effectiveInterval, setEffectiveInterval] =
    useState<PriceHistoryInterval>("1d");
  const [series, setSeries] = useState<MultiOutcomeHistorySeries[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [refetchToken, setRefetchToken] = useState(0);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    let cancelled = false;
    setError(null);

    // Smart fallback: try the user's selected interval first; if it
    // returns < MIN_VISIBLE_POINTS data points total, walk one step
    // wider in INTERVALS and try again, up to "all". Surfaces a
    // populated chart on low-liquidity windows instead of a blank
    // canvas. Pre-launch markets without any trades will still hit
    // "all" and end up empty — same as before.
    const tryFetch = async (range: PriceHistoryInterval): Promise<void> => {
      try {
        const res = await api.getMarketHistoryMulti(slug, { interval: range });
        if (cancelled) return;
        const outcomes = Array.isArray(res?.outcomes) ? res.outcomes : [];
        const totalPoints = outcomes.reduce(
          (sum, o) => sum + (o.points?.length ?? 0),
          0,
        );
        const wider = widerInterval(range);
        if (totalPoints < MIN_VISIBLE_POINTS && wider) {
          return tryFetch(wider);
        }
        setEffectiveInterval(range);
        setSeries(outcomes);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err.message
            : t("multiOutcomeLiveChart.loadFailed"),
        );
      }
    };
    void tryFetch(interval);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, interval, refetchToken]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Polling fallback. Skips when the tab is hidden (don't burn the
  // user's data plan + API budget on backgrounded tabs).
  useEffect(() => {
    const cadence = POLL_INTERVAL_MS[interval];
    const handle = window.setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      setRefetchToken((n) => n + 1);
    }, cadence);
    return () => window.clearInterval(handle);
  }, [interval]);

  // WS-driven live refetch. Mirrors LivePriceChart's WS shape.
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const apiBase = process.env.NEXT_PUBLIC_API_URL;
    if (!apiBase) return;
    const url = apiBase.replace(/\/$/, "");
    let socket: Socket | null = null;
    try {
      socket = io(url, {
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: 3,
        timeout: 8000,
      });
      socket.on("connect", () => {
        socket?.emit("subscribe", { slug });
      });
      socket.on("market:trade", (event: IncomingTradeEvent) => {
        if (event.marketSlug !== slug) return;
        if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = setTimeout(() => {
          setRefetchToken((n) => n + 1);
        }, 600);
      });
      socket.on("connect_error", () => {
        // swallow — polling fallback handles updates regardless.
      });
    } catch {
      // socket-io constructor doesn't throw, but be defensive.
    }

    return () => {
      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
      if (socket) {
        try {
          socket.emit("unsubscribe", { slug });
          socket.disconnect();
        } catch {
          // ignore — cleanup-time errors are harmless.
        }
      }
    };
  }, [slug]);

  return (
    <div>
      <div aria-live="polite">
        {error ? (
          <ChartError message={error} />
        ) : series === null ? (
          <ChartSkeleton />
        ) : !Array.isArray(series) || series.length === 0 ? (
          <ChartEmpty />
        ) : (
          <ChartCanvas series={series} viewerLocale={viewerLocale} />
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <div
          className="flex gap-0.5"
          role="tablist"
          aria-label={t("multiOutcomeLiveChart.rangeAriaLabel")}
        >
          {INTERVALS.map(({ id, label }) => {
            const active = id === interval;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setInterval(id)}
                className={`rounded-[4px] px-2 py-[3px] text-[10px] font-semibold transition-colors ${
                  active
                    ? "border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)]"
                    : "text-[var(--color-text3)] hover:text-[var(--color-text)]"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
        {effectiveInterval !== interval && (
          <span
            className="text-[10px] text-[var(--color-text3)]"
            title="Selected window had limited trade activity; showing a wider window instead."
          >
            Low data · showing{" "}
            <span className="font-semibold text-[var(--color-text2)]">
              {INTERVALS.find((i) => i.id === effectiveInterval)?.label ??
                effectiveInterval.toUpperCase()}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}

function ChartSkeleton() {
  const { t } = useTranslation();
  return (
    <div role="status" aria-label={t("multiOutcomeLiveChart.loadingAria")}>
      <Skeleton className="h-[260px] w-full rounded-md" />
    </div>
  );
}

function ChartError({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="flex h-[260px] w-full items-center justify-center rounded-md border border-dashed border-[var(--color-no)] bg-[var(--color-bg)] px-3 text-center text-[12px] text-[var(--color-no)]"
    >
      {message}
    </div>
  );
}

function ChartEmpty() {
  const { t } = useTranslation();
  return (
    <div
      className="flex h-[260px] w-full items-center justify-center rounded-md border border-dashed border-[var(--color-border)] bg-[var(--color-bg)] text-[12px] text-[var(--color-text3)]"
      aria-label={t("multiOutcomeLiveChart.noTradesAria")}
    >
      {t("multiOutcomeLiveChart.noTradesYet")}
    </div>
  );
}

// ─── Palette ────────────────────────────────────────────────────

// Eight distinct colors for the outcome lines. The first three are
// Granbay design tokens; the remaining five are Tailwind 500-scale
// stops chosen so adjacent colors stay distinguishable both in light
// and dark themes. Stable order — the same outcome index always gets
// the same color when the user changes time range.
const LINE_PALETTE: readonly string[] = [
  "var(--color-yes)", // green (#2d8a5d)
  "var(--color-no)", // red   (#b34a4a)
  "var(--color-brand)", // blue (theme-dependent: #0070f3 / #2d9cdb)
  "#a855f7", // purple — Tailwind violet-500
  "#f59e0b", // amber  — Tailwind amber-500
  "#06b6d4", // cyan   — Tailwind cyan-500
  "#ec4899", // pink   — Tailwind pink-500
  "#14b8a6", // teal   — Tailwind teal-500
];
const OTHER_COLOR = "#6b7280"; // gray — synthetic "Other" rollup

// Raw hex equivalents for the var()-based tokens — used in <stop>
// elements where CSS variables can't resolve to a paint server.
const LINE_PALETTE_RAW: readonly string[] = [
  "#2d8a5d",
  "#b34a4a",
  "#0070f3",
  "#a855f7",
  "#f59e0b",
  "#06b6d4",
  "#ec4899",
  "#14b8a6",
];

function colorForIndex(i: number, id: string): string {
  if (id === "other") return OTHER_COLOR;
  return LINE_PALETTE[i % LINE_PALETTE.length];
}

function rawColorForIndex(i: number, id: string): string {
  if (id === "other") return OTHER_COLOR;
  return LINE_PALETTE_RAW[i % LINE_PALETTE_RAW.length];
}

// ─── Canvas ────────────────────────────────────────────────────

interface HoverState {
  index: number;
  x: number;
}

function ChartCanvas({
  series,
  viewerLocale,
}: {
  series: MultiOutcomeHistorySeries[];
  viewerLocale: string;
}) {
  const { t } = useTranslation();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hover, setHover] = useState<HoverState | null>(null);
  const [width, setWidth] = useState(720);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.round(entry.contentRect.width);
        if (w > 0) setWidth(w);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Shared X-axis = union of every series' timestamps. Outcomes with
  // missing buckets carry-forward their last known price so the line
  // stays continuous; outcomes with NO points in the window stay flat
  // at the midpoint (50¢) and are flagged sparse in the legend.
  const { timeline, alignedSeries, sparseFlag } = useMemo(() => {
    const allTs = new Set<string>();
    for (const s of series) for (const p of s.points) allTs.add(p.ts);
    const tsList = Array.from(allTs).sort();
    if (tsList.length === 0) {
      return {
        timeline: [] as string[],
        alignedSeries: [] as AlignedSeries[],
        sparseFlag: new Map<string, boolean>(),
      };
    }

    const sparse = new Map<string, boolean>();
    const aligned: AlignedSeries[] = series.map((s) => {
      sparse.set(s.outcomeId, s.points.length < 2);
      const byTs = new Map<string, PriceHistoryPoint>();
      for (const p of s.points) byTs.set(p.ts, p);
      let last: number | null = null;
      const points = tsList.map((ts) => {
        const exact = byTs.get(ts);
        if (exact) {
          last = exact.priceCents;
          return exact.priceCents;
        }
        return last ?? s.points[0]?.priceCents ?? 50;
      });
      return {
        id: s.outcomeId,
        name: s.name,
        sortOrder: s.sortOrder,
        points,
      };
    });
    return { timeline: tsList, alignedSeries: aligned, sparseFlag: sparse };
  }, [series]);

  const w = width;
  const h = 260;
  const lineH = 220;
  const padX = 12;
  const padY = 16;
  const labelPad = 44;
  const innerW = Math.max(1, w - padX * 2 - labelPad);

  // Fixed 0-100% y-domain — every outcome's price is a probability and
  // the lines sum-to-one. Hard-coded so the axis doesn't auto-fit each
  // interval change (the user would lose the visual anchor).
  const yMin = 0;
  const yMax = 100;
  const yRange = yMax - yMin;

  const toX = (i: number) =>
    timeline.length <= 1
      ? padX + innerW / 2
      : padX + (i * innerW) / (timeline.length - 1);
  const toY = (v: number) =>
    padY + lineH - ((v - yMin) / yRange) * (lineH - padY);

  // Legend ordering — sort by latest probability descending so the
  // top line reads as the leader (Polymarket pattern).
  const legend = useMemo(() => {
    return alignedSeries
      .map((s, i) => {
        const lastPrice = s.points[s.points.length - 1] ?? 50;
        return {
          id: s.id,
          name: s.name,
          color: colorForIndex(i, s.id),
          lastPrice,
          isSparse: sparseFlag.get(s.id) === true,
          paletteIndex: i,
        };
      })
      .sort((a, b) => b.lastPrice - a.lastPrice);
  }, [alignedSeries, sparseFlag]);

  function handleMove(e: MouseEvent<SVGSVGElement>) {
    const svg = svgRef.current;
    if (!svg || timeline.length === 0) return;
    const rect = svg.getBoundingClientRect();
    const xRatio = (e.clientX - rect.left) / rect.width;
    const xViewport = xRatio * w;
    const indexF =
      timeline.length <= 1
        ? 0
        : ((xViewport - padX) / innerW) * (timeline.length - 1);
    const i = Math.max(0, Math.min(timeline.length - 1, Math.round(indexF)));
    setHover({ index: i, x: toX(i) });
  }
  function handleLeave() {
    setHover(null);
  }

  // 5 evenly-spaced y-ticks — 0 / 25 / 50 / 75 / 100%. Reads as the
  // canonical probability scale.
  const yTicks = [0, 25, 50, 75, 100];

  const monoStack = "var(--font-mono), ui-monospace, monospace";

  if (timeline.length === 0 || alignedSeries.length === 0) {
    return <ChartEmpty />;
  }

  return (
    <div ref={containerRef} className="relative">
      <svg
        ref={svgRef}
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        role="img"
        aria-label={t("multiOutcomeLiveChart.chartAria")}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
      >
        {/* Y-axis grid lines + % labels in the right gutter */}
        {yTicks.map((tick) => {
          const y = toY(tick);
          return (
            <g key={tick}>
              <line
                x1={padX}
                x2={w - labelPad}
                y1={y}
                y2={y}
                stroke="var(--color-border)"
                strokeOpacity="0.55"
                strokeDasharray="1 4"
              />
              <text
                x={w - labelPad + 6}
                y={y + 3}
                fontSize="10"
                fontWeight="500"
                fill="var(--color-text3)"
                fontFamily={monoStack}
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {tick}%
              </text>
            </g>
          );
        })}

        {/* One polyline per outcome — solid for normal, dotted for
            sparse (<2 points) and for the synthetic "Other" rollup. */}
        {alignedSeries.map((s, i) => {
          const color = colorForIndex(i, s.id);
          const sparse = sparseFlag.get(s.id) === true;
          const d = s.points
            .map(
              (p, idx) =>
                `${idx === 0 ? "M" : "L"}${toX(idx).toFixed(1)},${toY(p).toFixed(1)}`,
            )
            .join(" ");
          const isOther = s.id === "other";
          return (
            <path
              key={s.id}
              d={d}
              fill="none"
              stroke={color}
              strokeWidth={isOther ? 1.25 : 1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={sparse || isOther ? "3 3" : undefined}
              opacity={isOther ? 0.8 : 1}
            />
          );
        })}

        {/* Hover crosshair + per-series dot */}
        {hover && (
          <line
            x1={hover.x}
            x2={hover.x}
            y1={padY}
            y2={padY + lineH}
            stroke="var(--color-text3)"
            strokeOpacity="0.55"
            strokeWidth="1"
            strokeDasharray="2 3"
          />
        )}
        {hover &&
          alignedSeries.map((s, i) => (
            <circle
              key={`hover-${s.id}`}
              cx={hover.x}
              cy={toY(s.points[hover.index])}
              r="3.5"
              fill={rawColorForIndex(i, s.id)}
              stroke="var(--color-bg)"
              strokeWidth="1.25"
            />
          ))}
      </svg>

      {/* Hover tooltip — top-5 lines at the hovered timestamp, sorted
          by value descending so the leader reads first. */}
      {hover && timeline[hover.index] && (
        <div
          className="pointer-events-none absolute rounded-md border border-[var(--color-border)] bg-[var(--color-bg)]/95 px-2.5 py-1.5 text-[11px] shadow-lg backdrop-blur-sm"
          style={{
            left: Math.min(Math.max(hover.x, 80), w - 200),
            top: 4,
            transform: "translateX(-50%)",
            color: "var(--color-text)",
            whiteSpace: "nowrap",
            fontFamily: "var(--font-sans), system-ui, sans-serif",
            fontVariantNumeric: "tabular-nums",
            maxWidth: 240,
          }}
        >
          <div className="mb-1 text-[10px] text-[var(--color-text3)]">
            {new Date(timeline[hover.index]).toLocaleString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
              month: "short",
              day: "numeric",
            })}
          </div>
          {alignedSeries
            .map((s, i) => ({
              s,
              color: rawColorForIndex(i, s.id),
              v: s.points[hover.index],
            }))
            .sort((a, b) => b.v - a.v)
            .slice(0, 5)
            .map(({ s, color, v }) => (
              <div
                key={s.id}
                className="flex items-center gap-2 whitespace-nowrap"
              >
                <span
                  aria-hidden="true"
                  className="block h-2 w-2 rounded-full"
                  style={{ background: color }}
                />
                <span
                  className="flex-1 overflow-hidden text-ellipsis"
                  style={{ maxWidth: 150 }}
                >
                  {pickLocale(s.name, viewerLocale)}
                </span>
                <span
                  className="font-mono font-semibold"
                  style={{ fontFamily: monoStack }}
                >
                  {v}%
                </span>
              </div>
            ))}
        </div>
      )}

      {/* Legend — color swatch + outcome name + current price. Sparse
          outcomes (<2 data points in the time range) get a "not
          enough history" tag. */}
      <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[var(--color-text2)]">
        {legend.map((row) => {
          const label = pickLocale(row.name, viewerLocale);
          return (
            <li
              key={row.id}
              className="inline-flex items-center gap-1.5 whitespace-nowrap"
            >
              <span
                aria-hidden="true"
                className="block h-2 w-2 rounded-full"
                style={{
                  background: rawColorForIndex(row.paletteIndex, row.id),
                }}
              />
              <span className="text-[var(--color-text2)]">{label}</span>
              <span
                className="font-mono font-semibold text-[var(--color-text)]"
                style={{ fontFamily: monoStack }}
              >
                {row.lastPrice}%
              </span>
              {row.isSparse && (
                <span className="text-[10px] text-[var(--color-text3)]">
                  ({t("multiOutcomeLiveChart.notEnoughHistory")})
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface AlignedSeries {
  id: string;
  name: MultiOutcomeHistorySeries["name"];
  sortOrder: number;
  points: number[];
}
